import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAtomValue } from 'jotai';
import { debounce, sortBy } from 'es-toolkit';
import { useTranslation } from 'react-i18next';
import { Button, Dialog, InputAdornment, Stack, TextField, Typography } from '@mui/material';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import { koKR } from '@mui/x-data-grid/locales';
import { Delete, Edit, Search } from '@mui/icons-material';

import { endorsementsAtom } from '@/interfaces/site';
import { deleteEndorsementAtom } from '@/interfaces/endorsement';
import { SettingLayout } from '@/components/setting/SettingLayout';
import AddEndorsementDialog from '@/components/fda/AddEndorsementDialog';
import EditEndorsementDialog from '@/components/fda/EditEndorsementDialog';

import type { ChangeEvent } from 'react';
import type { GridColDef, GridRowParams } from '@mui/x-data-grid';
import type { EndorsementData } from '@/@types/site';

export default function EndorsementManagement() {
  const { data: endorsements, refetch } = useAtomValue(endorsementsAtom);
  const { mutate: deleteEndorsement, data: isDeleteSuccess } = useAtomValue(deleteEndorsementAtom);

  const [rows, setRows] = useState<EndorsementData[]>([]);
  const [query, setQuery] = useState('');
  const [dialog, setDialog] = useState('');
  const [selectedEndorsement, setSelectedEndorsement] = useState<EndorsementData>(null);
  const { t, i18n } = useTranslation();

  useEffect(() => {
    if (endorsements === undefined) return;
    setRows(sortBy(endorsements, ['name']));
  }, [endorsements]);
  useEffect(() => {
    if (isDeleteSuccess === undefined) return;
    if (isDeleteSuccess) {
      refetch();
    } else {
      window.alert(t('Common.message.serverErrorOccured'));
    }
  }, [isDeleteSuccess, refetch, t]);

  const locale = i18n.language.startsWith('ko') ? koKR.components.MuiDataGrid.defaultProps.localeText : null;
  const getActions = useCallback(
    (params: GridRowParams<EndorsementData>) => {
      const { id: endorsementId, name } = params.row;

      function onEditClick() {
        setDialog('EDIT_ENDORSEMENT');
        setSelectedEndorsement(params.row);
      }
      function onDeleteClick() {
        const ok = window.confirm(`${t('Common.message.confirmDelete')}\n${t('Common.label.name')}: ${name}`);
        if (ok) {
          deleteEndorsement(endorsementId);
        }
      }
      return [
        <GridActionsCellItem icon={<Edit fontSize="large" />} label="edit" onClick={onEditClick} />,
        <GridActionsCellItem icon={<Delete fontSize="large" color="error" />} label="delete" onClick={onDeleteClick} />,
      ];
    },
    [deleteEndorsement, t],
  );
  const columns = useMemo<GridColDef[]>(
    () => [
      { field: 'name', headerName: t('Common.label.name'), flex: 1, sortable: false },
      { field: 'description', headerName: t('Common.label.description'), flex: 1, sortable: false },
      {
        field: 'action',
        type: 'actions',
        headerName: t('Common.label.action'),
        flex: 0.5,
        sortable: false,
        getActions,
      },
    ],
    [getActions, t],
  );

  const filterEndorsements = useMemo(
    () =>
      debounce((query: string) => {
        if (query === '') {
          setRows(endorsements);
        } else {
          setRows(endorsements.filter((row) => row.name.toLowerCase().includes(query.toLowerCase())));
        }
      }, 500),
    [endorsements],
  );
  function onQueryChange(e: ChangeEvent<HTMLInputElement>) {
    const { value } = e.target;
    setQuery(value);
    filterEndorsements(value);
  }
  const onDialogClose = useCallback(
    (reason?: string) => {
      setDialog('');

      if (reason === 'updated') {
        refetch();
      }
    },
    [refetch],
  );

  return (
    <SettingLayout>
      <Stack gap={3} sx={{ height: '100%' }}>
        <Typography variant="h5" fontWeight="bold">
          {t('FDA.label.endorsementManagement')}
        </Typography>
        <Stack direction="row" justifyContent="space-between">
          <TextField
            size="small"
            placeholder={t('Common.label.name')}
            value={query}
            onChange={onQueryChange}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              },
            }}
            sx={{ width: '300px', bgcolor: 'white' }}
          />
          <Button variant="contained" onClick={() => setDialog('ADD_ENDORSEMENT')}>
            {t('FDA.label.addEndorsement')}
          </Button>
        </Stack>

        <DataGrid
          disableColumnMenu
          disableColumnResize
          disableRowSelectionOnClick
          localeText={locale}
          columns={columns}
          rows={rows}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
        />
      </Stack>

      <Dialog open={dialog === 'ADD_ENDORSEMENT'} onClose={onDialogClose}>
        <AddEndorsementDialog onClose={onDialogClose} />
      </Dialog>
      <Dialog open={dialog === 'EDIT_ENDORSEMENT'} onClose={onDialogClose}>
        <EditEndorsementDialog onClose={onDialogClose} endorsement={selectedEndorsement} />
      </Dialog>
    </SettingLayout>
  );
}
